/**
 * Pure, testable helpers for the Capture / Notepad inbox.
 * No React, no side effects — safe to unit test in isolation.
 * (Exception: getLastCaptureType/setLastCaptureType read/write localStorage
 * by design (CAP-4) — still framework-free and unit-testable via jsdom's
 * localStorage, just not side-effect-free.)
 */

import { normalizeCaptureSearch } from "./captureSearchNormalize";
import { captureLinkError, normalizeCaptureLink } from "./captureLinks";

export const CAPTURE_TYPES = {
  TEXT: "text",
  PHOTO: "photo",
  VIDEO: "video",
  AUDIO: "audio",
  DOC: "doc",
  // QA-98 — a capture whose payload is a URL, not an uploaded object.
  LINK: "link",
};

export { ATTACH_DETACH_WHEELS_LABEL } from "./captureLabels";

/** Max upload size for a captured photo or document, in MB. */
export const MAX_CAPTURE_FILE_MB = 25;

/**
 * Max size for a captured VIDEO file, in MB. Larger than photo/doc because video
 * is heavy; mirrors the in-browser recorder's own blob cap
 * (useMediaRecorder.MAX_RECORDING_BYTES).
 *
 * NOTE (measured 2026-08-04, iYuga-backend @ dc4c7c6fb): the backend's
 * presigned-URL serializer (`users/serializers.py::PresignedUrlSerializer`)
 * validates the object PATH and EXTENSION only — it has NO `object_size` /
 * byte-cap validation at all. These constants are therefore the ONLY size guard
 * in the product, not a mirror of a stricter server rule.
 */
export const MAX_CAPTURE_AV_MB = 100;

/**
 * Max size for a captured AUDIO file, in MB (QA-95). 100 MB of audio is hours
 * long — far more than a reasonable single recording — so it guarded nothing.
 * Kept in step with `useMediaRecorder.MAX_AUDIO_RECORDING_BYTES` (15 MB) so the
 * upload path and the record path never quote the user two different limits.
 */
export const MAX_CAPTURE_AUDIO_MB = 15;

/** MB cap for a given capture type (media weight differs by kind). */
export const maxSizeMbForType = (type) => {
  if (type === CAPTURE_TYPES.VIDEO) return MAX_CAPTURE_AV_MB;
  if (type === CAPTURE_TYPES.AUDIO) return MAX_CAPTURE_AUDIO_MB;
  return MAX_CAPTURE_FILE_MB;
};

/**
 * Detect the capture type from a browser File.
 * image/* -> photo; video/* -> video; audio/* -> audio; anything else -> doc.
 */
export const detectCaptureType = (file) => {
  if (!file || !file.type) return CAPTURE_TYPES.DOC;
  if (file.type.startsWith("image/")) return CAPTURE_TYPES.PHOTO;
  if (file.type.startsWith("video/")) return CAPTURE_TYPES.VIDEO;
  if (file.type.startsWith("audio/")) return CAPTURE_TYPES.AUDIO;
  return CAPTURE_TYPES.DOC;
};

/**
 * S3 key prefix for capture uploads — mirrors getWheelUploadPath
 * (`uploads/{userId}/...`) so media_key matches the backend contract.
 */
export const getCaptureUploadPath = (userId) => `uploads/${userId}/capture/`;

/** Flatten react-query infinite pages into a single results array. */
export const flattenCapturePages = (data) =>
  (data?.pages ?? []).flatMap((page) => page?.results ?? []);

/** Pure size guard (defaults to MAX_CAPTURE_FILE_MB). */
export const isWithinSizeLimit = (file, maxMb = MAX_CAPTURE_FILE_MB) =>
  !!file && typeof file.size === "number" && file.size <= maxMb * 1024 * 1024;

/** Human-readable label for a capture item type. */
export const captureTypeLabel = (type) => {
  switch (type) {
    case CAPTURE_TYPES.PHOTO:
      return "Photo";
    case CAPTURE_TYPES.VIDEO:
      return "Video";
    case CAPTURE_TYPES.AUDIO:
      return "Audio";
    case CAPTURE_TYPES.DOC:
      return "Document";
    case CAPTURE_TYPES.LINK:
      return "Link";
    default:
      return "Note";
  }
};

/* ----- QA-82 / QA-69 / QA-90: renaming a captured file ------------------- */

/**
 * Split a filename into its base and its extension (the extension keeps its
 * leading dot; a name with no dot gets `""`). A leading-dot name (".env") is
 * treated as all base — it has no extension, it IS the name.
 */
export const splitFileName = (name = "") => {
  const value = String(name || "");
  const dot = value.lastIndexOf(".");
  if (dot <= 0) return { base: value, ext: "" };
  return { base: value.slice(0, dot), ext: value.slice(dot) };
};

/** The editable part of a filename — what the rename field is seeded with. */
export const defaultCaptureName = (fileName = "") => splitFileName(fileName).base;

/**
 * Apply a user-typed name to a file, PRESERVING the original extension.
 *
 * The extension is not the user's to change: it is what the backend's
 * per-capture-type allowlist matches on (`_EXTENSIONS_BY_TYPE`), so letting a
 * rename drop or alter it would turn a valid clip into a 400. A blank/whitespace
 * name falls back to the original filename rather than producing ".mp4".
 */
export const applyCaptureName = (originalName = "", typedName = "") => {
  const { base, ext } = splitFileName(originalName);
  const typed = String(typedName || "").trim();
  if (!typed) return `${base}${ext}`;
  // Strip a re-typed extension so "clip.mp4" doesn't become "clip.mp4.mp4".
  const typedBase = ext && typed.toLowerCase().endsWith(ext.toLowerCase())
    ? typed.slice(0, -ext.length)
    : typed;
  return `${typedBase.trim() || base}${ext}`;
};

/**
 * Validate composer state before submit.
 * Returns an error string, or null when valid.
 *
 * `pendingFile` is a file the user has acquired (picked, shot on the webcam, or
 * recorded) that has NOT been uploaded yet — under the two-phase composer the
 * upload happens when Capture is pressed, so submittability can no longer key
 * off `mediaKey` alone or the button would be dead for every fresh capture.
 */
export const validateComposer = ({ type, name, textContent, mediaKey, sourceUrl } = {}) => {
  if (type === CAPTURE_TYPES.TEXT) {
    // ISP-9999: a quick note (Title only, no Notes body) is the common case --
    // Notes isn't marked required, so either field being filled is enough.
    const hasTitle = name && name.trim();
    const hasNotes = textContent && textContent.trim();
    return hasTitle || hasNotes ? null : "Write something to capture.";
  }
  if (type === CAPTURE_TYPES.LINK) {
    // QA-98: the URL IS the payload, so the composer is invalid without one —
    // and the specific reason is surfaced rather than a generic refusal.
    return captureLinkError(sourceUrl);
  }
  return mediaKey ? null : "Add a file to capture.";
};

const rememberAttachedWheel = (byId, id, name = "") => {
  if (id == null || id === "") return;
  const key = String(id);
  const trimmed = String(name || "").trim();
  const prev = byId.get(key);
  if (!prev) {
    byId.set(key, { id, name: trimmed });
    return;
  }
  if (!prev.name && trimmed) prev.name = trimmed;
};

/**
 * Every wheel this capture is on, for inbox chips and attach-duplicate checks.
 *
 * Prefers `attached_wheels` `[{id, name}]` from the M2M list payload, then
 * folds in `attached_wheel_ids` and the legacy singular `attached_wheel` /
 * `attached_wheel_name` so an older cached row still produces at least the
 * primary chip. Insertion order matches the API list, then any leftover ids.
 */
export const attachedWheelsList = (item) => {
  if (!item) return [];
  const byId = new Map();

  if (Array.isArray(item.attached_wheels)) {
    item.attached_wheels.forEach((wheel) => {
      if (wheel == null) return;
      if (typeof wheel !== "object") {
        rememberAttachedWheel(byId, wheel);
        return;
      }
      rememberAttachedWheel(byId, wheel.id, wheel.name);
    });
  }
  if (Array.isArray(item.attached_wheel_ids)) {
    item.attached_wheel_ids.forEach((id) => rememberAttachedWheel(byId, id));
  }
  rememberAttachedWheel(
    byId,
    typeof item.attached_wheel === "object"
      ? item.attached_wheel?.id
      : item.attached_wheel,
    item.attached_wheel_name ||
      (typeof item.attached_wheel === "object" ? item.attached_wheel?.name : "")
  );

  return [...byId.values()];
};

/** Visible inbox chip plus the extra wheels collapsed behind `+N`. */
export const attachedWheelsPreview = (item) => {
  const wheels = attachedWheelsList(item);
  if (!wheels.length) return { shown: null, extra: [] };
  return { shown: wheels[0], extra: wheels.slice(1) };
};

/** True when this capture is on `wheelId` (M2M list or legacy singular). */
export const itemIsOnWheel = (item, wheelId) =>
  wheelId != null &&
  wheelId !== "" &&
  attachedWheelsList(item).some((wheel) => String(wheel.id) === String(wheelId));

/** True when the item is linked to at least one wheel. */
export const isAttached = (item) => attachedWheelsList(item).length > 0;

/** QA-115 chip copy for one wheel: the wheel name, else "Attached". */
export const attachedWheelChipLabel = (wheel) => {
  const name = (wheel?.name || "").trim();
  return name || "Attached";
};

/** Inbox chip when extra wheels collapse behind +N: "Foo +3". */
export const attachedWheelChipLabelWithExtra = (wheel, extraCount = 0) => {
  const base = attachedWheelChipLabel(wheel);
  const extra = Number(extraCount) || 0;
  return extra > 0 ? `${base} +${extra}` : base;
};

/**
 * ISP-9999: numbered lines for the inbox +N attached-wheels tooltip.
 * Hovering +2 used to dump names as one blob; list them 1. 2. 3. 4.
 * so each extra wheel is a readable row (1. A\n2. B).
 */
export const numberedWheelTooltip = (wheels = []) =>
  wheels
    .map((wheel, index) => `${index + 1}. ${attachedWheelChipLabel(wheel)}`)
    .join("\n");

/**
 * QA-115 (Kelly): the label for an attached capture's chip.
 *
 * "When a capture is attached to a Wheel it would be very helpful to know which
 * Wheel name it is attached to." So when the backend supplies the attached
 * wheel's name (`attached_wheel_name`), show the wheel name; otherwise fall
 * back to the plain "Attached" (an unattached item, an untitled wheel, or an
 * older backend that does not yet send the name). Pure + testable.
 * Returns null when the item is not attached at all (no chip is rendered).
 *
 * Inbox rows now render one chip per wheel via `attachedWheelsList`; this
 * helper stays the primary-wheel label for older callers.
 */
export const attachedWheelLabel = (item) => {
  if (!isAttached(item)) return null;
  const name = (item?.attached_wheel_name || "").trim();
  if (name) return name;
  const firstNamed = attachedWheelsList(item).find((wheel) => wheel.name);
  return firstNamed ? firstNamed.name : "Attached";
};

/**
 * Capture types that may carry an OPTIONAL free-text note (caption).
 *
 * QA-90 (Gordon, 2026-08-04): "the rename + add-a-note step is a UNIVERSAL
 * requirement of all Capture modals (every media type, upload or record)" — so
 * this is now EVERY media type, not just photo/doc.
 *
 * DEPLOY ORDER (load-bearing): the backend rejects `text_content` on VIDEO and
 * AUDIO today — `capture/api/serializers.py` 400s with "Not allowed on a media
 * capture", `capture/models.py::clean` raises, and the DB CheckConstraint
 * `capture_payload_matches_type` forbids the row outright. The matching backend
 * PR MUST reach app-stg BEFORE this frontend ships, or a note typed on a
 * video/audio capture 400s the create. `describeCaptureSaveError` below turns
 * that specific 400 into an actionable message instead of a dead end, so the
 * wrong order degrades to a clear instruction rather than a lost clip.
 */
const NOTE_BEARING_MEDIA_TYPES = [
  CAPTURE_TYPES.PHOTO,
  CAPTURE_TYPES.DOC,
  CAPTURE_TYPES.VIDEO,
  CAPTURE_TYPES.AUDIO,
];

/**
 * Turn a failed capture save into something the user can ACT on.
 *
 * The one case worth special-casing is the backend's "Not allowed on a media
 * capture" 400 — it fires only when the note-widening backend PR is not
 * deployed yet, and the generic "Could not save" message would leave the user
 * retrying forever with no idea the note is what is being rejected. The staged
 * S3 object is untouched either way, so clearing the note and pressing Capture
 * again always works.
 */
export const describeCaptureSaveError = (error) => {
  const data = error?.response?.data;
  const noteError = data?.text_content;
  const text = Array.isArray(noteError) ? noteError.join(" ") : String(noteError || "");
  if (text.toLowerCase().includes("not allowed on a media capture")) {
    return "Notes aren't available on video and audio captures yet. Clear the note and tap Capture again — your file is still here.";
  }
  return data?.message || "Could not save. Please try again.";
};

/**
 * Gordon priority fix (2026-08-06) — Task 2. Capture types that carry the
 * NEW, separate `name` field (distinct from the note/caption above): all four
 * media types, never TEXT (no file to name) and never LINK (out of scope for
 * this pass — QA-98's note field already serves as its title).
 *
 * BE DEPENDENCY: `name` lands in a parallel Backend PR whose staging deploy
 * is Actions-budget-blocked as of this change. Sending it here is safe either
 * way -- an unknown field is ignored by the create serializer until that PR
 * deploys, and a missing `item.name` on read simply falls back to "".
 */
const NAME_BEARING_MEDIA_TYPES = [
  CAPTURE_TYPES.TEXT,
  CAPTURE_TYPES.PHOTO,
  CAPTURE_TYPES.VIDEO,
  CAPTURE_TYPES.AUDIO,
  CAPTURE_TYPES.DOC,
  CAPTURE_TYPES.LINK,
];

/**
 * Types the composer/edit flow supports.
 *
 * QA-119 #2/#3 (Kelly): VIDEO/AUDIO and LINK previously had NO edit
 * affordance at all. Now every type is fully editable:
 *  - VIDEO/AUDIO: the backend's CaptureItemUpdateSerializer accepts a
 *    text_content PATCH (note/name) AND an in-place `media_key` replacement
 *    (Backend #2509, Gordon 2026-08-18: "add ability to replace Video and
 *    Audio") -- so editing one shows the SAME full media composer photo/doc
 *    get, with the Replace button and the note/name fields.
 *  - LINK: the backend already accepts a `source_url` PATCH (QA-98/QA-99),
 *    so editing one just reopens the SAME Link tab UI a new capture uses.
 */
export const isEditableCaptureType = (type) =>
  [
    CAPTURE_TYPES.TEXT,
    CAPTURE_TYPES.PHOTO,
    CAPTURE_TYPES.DOC,
    CAPTURE_TYPES.VIDEO,
    CAPTURE_TYPES.AUDIO,
    CAPTURE_TYPES.LINK,
  ].includes(type);

/**
 * Build the POST /capture/items/ create payload from composer draft state.
 *
 * Backend contract (capture/api/serializers.py CaptureItemCreateSerializer +
 * the `capture_payload_matches_type` DB CheckConstraint):
 *  - TEXT      → requires text_content, no media_key.
 *  - PHOTO/DOC → require media_key; may ALSO carry an OPTIONAL free-text note in
 *    text_content. Row-17 QA (Terry+Kelly): a note typed on a photo/document
 *    MUST PERSIST — so we send it when present.
 *  - VIDEO/AUDIO → require media_key and REJECT text_content: the serializer
 *    400s ("Not allowed on a media capture") if a note is present, and there is
 *    no partial save — the WHOLE create fails and the user loses the clip. So we
 *    NEVER send text_content on video/audio, even if a note somehow slipped
 *    through (the composer already hides the caption field on those tabs).
 *
 * Blank notes are omitted entirely. Returns { payload }.
 */
export const buildCreateCapturePayload = ({
  type,
  textContent,
  mediaKey,
  sourceUrl,
  name,
} = {}) => {
  const note = (textContent || "").trim();
  const payload = { type };
  const trimmedTitle = (name || "").trim();
  const withTitle = (p) => {
    if (trimmedTitle && NAME_BEARING_MEDIA_TYPES.includes(type)) {
      p.title = trimmedTitle;
    }
    return p;
  };
  if (type === CAPTURE_TYPES.TEXT) {
    if (note) payload.text_content = note;
    return { payload: withTitle(payload) };
  }
  if (type === CAPTURE_TYPES.LINK) {
    // QA-98: no media_key at all — a link was never uploaded, so inventing an
    // object key for it would be a lie the backend rightly rejects.
    payload.source_url = normalizeCaptureLink(sourceUrl);
    if (note) payload.text_content = note;
    return { payload: withTitle(payload) };
  }
  payload.media_key = mediaKey;
  // QA-99: an uploaded capture that CAME from an embed/share URL records where
  // it came from, so an embedded video can always show its origin.
  const provenance = normalizeCaptureLink(sourceUrl);
  if (provenance) payload.source_url = provenance;
  // QA-90/QA-82: persist the note for EVERY media type now that the backend
  // accepts text_content on VIDEO/AUDIO (Backend #1863, merged 2026-08-05).
  // Blank notes are still omitted entirely, so a user who types nothing sends
  // nothing and the payload is byte-identical to before on every type.
  if (note && NOTE_BEARING_MEDIA_TYPES.includes(type)) {
    payload.text_content = note;
  }
  withTitle(payload);
  return { payload };
};

/**
 * In-place media replacement is supported for EVERY media-key-bearing type —
 * PHOTO, DOC, VIDEO and AUDIO (Gordon 2026-08-18: "Capture web add ability to
 * replace Video and Audio"). This reverses the earlier K2b carve-out (Gordon
 * 2026-08-11) that forbade replacing an av recording: the backend's
 * `CaptureItemUpdateSerializer` now accepts a `media_key` swap on VIDEO/AUDIO
 * too (Backend #2509 — extension-validated per type, and it regenerates the
 * VIDEO poster at the new key). The note/name (`text_content`) stays editable
 * on every type, unchanged.
 */

/**
 * Build the PATCH /capture/items/{id}/ payload for editing an existing item
 * (CAP-3) -- a DELTA of only the fields that actually changed, so an edit
 * that only touches the note doesn't also resend an unchanged media_key (and
 * a media replace doesn't force-touch an unrelated note). `changed` is false
 * when the composer was opened and closed with no edits made -- callers
 * should skip the API call entirely in that case (no pointless PATCH, no
 * misleading "updated" toast for a no-op).
 */
export const buildUpdateCapturePayload = (original = {}, draft = {}) => {
  const payload = {};

  // The note/name (text_content) is editable on every type — for TEXT it is the
  // note, for a media item it is the caption/name (captureDisplayName reads it
  // first), and for VIDEO/AUDIO too (K2b): the backend now accepts a text_content
  // PATCH on them.
  {
    const originalNote = (original.text_content || "").trim();
    const draftNote = (draft.textContent || "").trim();
    if (draftNote !== originalNote) {
      payload.text_content = draftNote;
    }
  }

  // QA-119 #3: a LINK's payload IS its url -- editing one means PATCHing
  // source_url, the same field create sends (QA-98/QA-99). The backend
  // already accepts this; only the delta was ever missing here.
  if (draft.type === CAPTURE_TYPES.LINK) {
    const originalUrl = (original.source_url || "").trim();
    const draftUrl = (draft.sourceUrl || "").trim();
    if (draftUrl !== originalUrl) {
      payload.source_url = normalizeCaptureLink(draftUrl);
    }
  }

  // Media replacement is for every media-key-bearing type — PHOTO, DOC, VIDEO
  // and AUDIO (Gordon 2026-08-18) — never TEXT/LINK (no uploaded object).
  if (
    draft.type !== CAPTURE_TYPES.TEXT &&
    draft.type !== CAPTURE_TYPES.LINK
  ) {
    const originalKey = original.media_key || "";
    const draftKey = draft.mediaKey || "";
    if (draftKey && draftKey !== originalKey) {
      payload.media_key = draftKey;
    }
  }

  // Task 2 (2026-08-06): PATCH the Name field only when it actually changed,
  // matching the note/media_key delta pattern above -- and only for the four
  // media types (never TEXT, never LINK, same scope as create).
  if (NAME_BEARING_MEDIA_TYPES.includes(draft.type)) {
    const originalName = (original.title || original.name || "").trim();
    const draftName = (draft.name || "").trim();
    if (draftName !== originalName) {
      payload.title = draftName;
    }
  }

  return { payload, changed: Object.keys(payload).length > 0 };
};

/* ----- CAP-4: remember the last media type chosen in the composer ----- */

const LAST_TYPE_STORAGE_KEY = "iyuga.capture.lastType";

/** Only these three types are ever offered by the composer's tabs. */
const VALID_COMPOSER_TYPES = [CAPTURE_TYPES.TEXT, CAPTURE_TYPES.PHOTO, CAPTURE_TYPES.DOC];

/**
 * Read the last capture type the user picked in the "New capture" composer,
 * so reopening it defaults to that tab instead of always resetting to Text.
 * Falls back to TEXT on first run, a corrupted/stale value, or when storage
 * is unavailable (private browsing) -- never throws.
 */
export const getLastCaptureType = () => {
  try {
    const stored = window.localStorage.getItem(LAST_TYPE_STORAGE_KEY);
    return VALID_COMPOSER_TYPES.includes(stored) ? stored : CAPTURE_TYPES.TEXT;
  } catch {
    return CAPTURE_TYPES.TEXT;
  }
};

/** Persist the type the user just picked. Best-effort; never throws. */
export const setLastCaptureType = (type) => {
  if (!VALID_COMPOSER_TYPES.includes(type)) return;
  try {
    window.localStorage.setItem(LAST_TYPE_STORAGE_KEY, type);
  } catch {
    // Storage unavailable (private mode / quota) -- non-fatal, just won't persist.
  }
};

/* ----- attach-wheel picker: duplicate-add warning (CAP-7) ----- */

/**
 * Every wheel id a single item is attached to. `attached_wheel_ids` (plural)
 * is the full multi-wheel set the M2M backend serves (ISP-9999); the legacy
 * singular `attached_wheel` is folded in too so an older cached item shape
 * (or a not-yet-refetched row) is never treated as attached to nothing.
 */
const itemWheelIds = (it) => attachedWheelsList(it).map((wheel) => wheel.id);

/**
 * Wheel IDs at least one item in the batch is already attached to. Drives the
 * picker's "Already added" badge on each matching wheel row.
 *
 * BUG-capture-96a126 (Kelly QA-138 #4): reading only the legacy singular
 * `attached_wheel` missed every OTHER wheel a capture is on once a capture
 * could be attached to many wheels at once -- the badge only ever lit up its
 * primary wheel, not "every wheel that already contains the capture."
 */
export const attachedWheelIdSet = (items = []) =>
  new Set((items || []).flatMap(itemWheelIds));

/**
 * True when EVERY item in the batch is already attached to `wheelId` --
 * re-attaching would be a pure no-op, so the caller should warn instead of
 * firing the mutation (and the misleading "Linked to wheel." success toast
 * that would otherwise follow). Same multi-wheel awareness as
 * attachedWheelIdSet: a wheel reached only through `attached_wheel_ids`
 * still counts as "already attached."
 */
export const isDuplicateAttach = (items = [], wheelId) =>
  wheelId != null &&
  (items || []).length > 0 &&
  items.every((it) => itemIsOnWheel(it, wheelId));

/**
 * Bulk attach picker — how many selected captures are already on one wheel.
 * Drives the yellow "N/M Captures Already Attached" pill in bulk mode (replaces
 * the green pill whenever 2+ captures are selected).
 *
 * @returns {{ attached: number, total: number, label: string } | null}
 */
export const bulkWheelPartialAttachStatus = (items = [], wheelId) => {
  const list = (items || []).filter(Boolean);
  const total = list.length;
  if (total === 0 || wheelId == null) return null;
  const attached = list.filter((it) => itemIsOnWheel(it, wheelId)).length;
  return {
    attached,
    total,
    label: `${attached}/${total} attached`,
  };
};

/**
 * Tri-state checkbox visual for bulk Add-to-Wheel rows — reflects how many of
 * the CURRENTLY SELECTED captures are on this wheel (not operation selection).
 *
 * @returns {'none'|'partial'|'all'}
 */
export const wheelBulkAttachCheckboxState = (items = [], wheelId) => {
  const status = bulkWheelPartialAttachStatus(items, wheelId);
  if (!status || status.attached === 0) return "none";
  if (status.attached === status.total) return "all";
  return "partial";
};

/* ----- QA-133: bulk "Add to Wheel" = idempotent UNION ----- */

/**
 * QA-133 (Gordon, 2026-08-11): a bulk "Add to Wheel" over a MIXED selection —
 * some captures already on the target wheel, some not — must behave as a UNION:
 * the not-attached ones become attached, the already-attached ones simply STAY
 * attached (a success, not an error, and not a duplicate re-attach).
 *
 * This planner splits the selection so the caller attaches each capture AT MOST
 * ONCE: only the ones that are not already on the target are sent. The
 * already-on-target ones are reported back so the result summary can count them
 * toward the union without firing a redundant mutation for them — which also
 * means the FE does not DEPEND on the backend attach endpoint being idempotent
 * for the common case (a capture is only re-sent to a wheel it is not on).
 *
 * `wheelId` null means "detach" — there is no union to plan, so everything is
 * returned as `toAttach` (the detach path attaches to `null`).
 *
 * Pure — no dispatch, no DOM.
 *
 * @param {object[]} items   the selected capture rows
 * @param {number|string|null} wheelId  the target wheel id (null = detach)
 * @returns {{ toAttach: object[], alreadyOnTarget: object[] }}
 */
export const planBulkAttach = (items = [], wheelId) => {
  const list = (items || []).filter(Boolean);
  if (wheelId == null) return { toAttach: list, alreadyOnTarget: [] };
  const alreadyOnTarget = list.filter((it) => it.attached_wheel === wheelId);
  const toAttach = list.filter((it) => it.attached_wheel !== wheelId);
  return { toAttach, alreadyOnTarget };
};

/**
 * bug #305 — the multi-wheel generalization of planBulkAttach: UNION over
 * every (item, wheel) PAIR instead of just items against one target.
 *
 * Same idempotent-union contract, extended to N target wheels: a pair already
 * on that wheel (M2M-aware, via itemIsOnWheel/attachedWheelsList — not just
 * the legacy singular `attached_wheel`) is counted, not re-sent, so selecting
 * 3 captures x 2 wheels never fires more than the (up to) 6 attach calls the
 * selection actually needs.
 *
 * @param {object[]} items
 * @param {Array<number|string>} wheelIds
 * @returns {{ pairs: Array<{item: object, wheelId: number|string}>, alreadyOnTargetCount: number }}
 */
export const planBulkAttachToWheels = (items = [], wheelIds = []) => {
  const list = (items || []).filter(Boolean);
  const wheels = (wheelIds || []).filter((id) => id != null);
  let alreadyOnTargetCount = 0;
  const pairs = [];
  list.forEach((item) => {
    wheels.forEach((wheelId) => {
      if (itemIsOnWheel(item, wheelId)) {
        alreadyOnTargetCount += 1;
      } else {
        pairs.push({ item, wheelId });
      }
    });
  });
  return { pairs, alreadyOnTargetCount };
};

/**
 * Bulk detach planner — one `{ item, wheelIds }` entry per capture that is on
 * at least one of the checked wheels. Uses the array-based
 * `/detach-wheels/` payload shape (`wheel_ids: [...]`).
 *
 * @param {object[]} items
 * @param {Array<number|string>} wheelIds checked wheels in the picker
 * @returns {{ pairs: Array<{ item: object, wheelIds: Array<number|string> }>, linkCount: number }}
 */
export const planBulkDetachFromWheels = (items = [], wheelIds = []) => {
  const list = (items || []).filter(Boolean);
  const wheels = (wheelIds || []).filter((id) => id != null);
  const pairs = [];
  let linkCount = 0;
  list.forEach((item) => {
    const ids = wheels.filter((wheelId) => itemIsOnWheel(item, wheelId));
    if (ids.length > 0) {
      pairs.push({ item, wheelIds: ids });
      linkCount += ids.length;
    }
  });
  return { pairs, linkCount };
};

/**
 * Compare the wheel ids checked in the picker against the ids that were checked
 * when the modal opened — drives attach-only / detach-only Save without
 * re-sending links that did not change.
 *
 * @returns {{ toAttachWheelIds: Array, toDetachWheelIds: Array, hasChanges: boolean }}
 */
export const wheelSelectionDelta = (initialWheelIds = [], selectedWheelIds = []) => {
  const initial = (initialWheelIds || []).filter((id) => id != null);
  const selected = (selectedWheelIds || []).filter((id) => id != null);
  const initialSet = new Set(initial.map(String));
  const selectedSet = new Set(selected.map(String));
  const toAttachWheelIds = selected.filter((id) => !initialSet.has(String(id)));
  const toDetachWheelIds = initial.filter((id) => !selectedSet.has(String(id)));
  return {
    toAttachWheelIds,
    toDetachWheelIds,
    hasChanges: toAttachWheelIds.length > 0 || toDetachWheelIds.length > 0,
  };
};

/**
 * Plan attach + detach mutations for a picker Save from initial vs final ids.
 *
 * @returns {{
 *   attachPairs: Array<{item: object, wheelId: number|string}>,
 *   detachPairs: Array<{item: object, wheelIds: Array<number|string>}>,
 *   hasChanges: boolean,
 * }}
 */
export const planWheelPickerSave = (items = [], initialWheelIds = [], selectedWheelIds = []) => {
  const { toAttachWheelIds, toDetachWheelIds, hasChanges } = wheelSelectionDelta(
    initialWheelIds,
    selectedWheelIds
  );
  const { pairs: attachPairs } = planBulkAttachToWheels(items, toAttachWheelIds);
  const { pairs: detachPairs } = planBulkDetachFromWheels(items, toDetachWheelIds);
  return { attachPairs, detachPairs, hasChanges };
};

/**
 * Validate the attach-wheel picker before Save — blocked only while loading,
 * on error, with no wheels, or with nothing selected on a never-attached item.
 */
export const validateWheelPickerSave = ({
  selected = [],
  initialWheelIds = [],
  wheelsLoading = false,
  wheelsError = false,
  wheelCount = 0,
} = {}) => {
  if (wheelsLoading) return "Loading your wheels…";
  if (wheelsError) return "We couldn't load your wheels. Retry, then pick one.";
  if (wheelCount === 0) {
    return "You don't have any wheels yet — create one first.";
  }
  const selectedIds = Array.isArray(selected) ? selected : [];
  if (selectedIds.length === 0 && initialWheelIds.length === 0) {
    return "Pick a wheel to add to.";
  }
  return null;
};

/**
 * User-facing summary after a bulk detach from checked wheels.
 *
 * @param {{ detached: number, failed: number, captureCount?: number, wheelCount?: number }} result
 */
export const bulkDetachResultMessage = ({
  detached = 0,
  failed = 0,
  captureCount,
  wheelCount = 1,
} = {}) => {
  const useBulkCopy = typeof captureCount === "number" && captureCount >= 2;
  const captureWord = (n) => (n === 1 ? "capture" : "captures");
  const wheelWord = (n) => (n === 1 ? "wheel" : "wheels");

  if (useBulkCopy) {
    if (failed === 0) {
      return {
        tone: "success",
        message: `Detached ${detached} ${captureWord(detached)} from ${wheelCount} ${wheelWord(wheelCount)}.`,
      };
    }
    if (detached === 0) {
      return {
        tone: "error",
        message: `Could not detach ${captureCount} ${captureWord(captureCount)} from ${wheelCount} ${wheelWord(wheelCount)}. Please try again.`,
      };
    }
    return {
      tone: "warning",
      message: `Detached ${detached} ${captureWord(detached)} from ${wheelCount} ${wheelWord(wheelCount)}. The rest could not be detached — please try again.`,
    };
  }

  if (failed === 0) {
    return {
      tone: "success",
      message:
        detached === 1
          ? "Capture detached."
          : `Detached ${detached} capture link${detached === 1 ? "" : "s"}.`,
    };
  }
  if (detached === 0) {
    return {
      tone: "error",
      message: "Could not detach. Please try again.",
    };
  }
  return {
    tone: "warning",
    message: `Detached ${detached} of ${detached + failed} links. The rest could not be detached — please try again.`,
  };
};

/**
 * True when a failed attach is the backend refusing an ALREADY-ATTACHED
 * capture. The FE treats this as a success (the capture is on the wheel, which
 * is the outcome the user asked for), so one racing/stale already-attached row
 * cannot fail an entire union batch.
 *
 * NOTE (route to Usman/Amir): the correct backend behaviour is for
 * POST .../{id}/attach/ to be IDEMPOTENT — re-attaching a capture to a wheel it
 * is already on should be a 200 no-op, never a 4xx. This matcher is the FE's
 * defensive net until that lands; if the backend already 200s, it simply never
 * fires. Deliberately narrow (matches the "already attached/added/on" wording
 * only) so it can never swallow an unrelated real failure.
 */
export const isAlreadyAttachedError = (error) => {
  const data = error?.response?.data;
  const parts = [
    data?.message,
    data?.detail,
    data?.wheel_id,
    Array.isArray(data?.non_field_errors) ? data.non_field_errors.join(" ") : data?.non_field_errors,
    typeof data === "string" ? data : null,
  ]
    .flat()
    .filter(Boolean)
    .join(" ");
  return /already\s+(attached|added|on)\b/i.test(parts);
};

/**
 * Summarize the outcome of a union bulk-attach for the toast layer.
 *
 * `attached` = items whose mutation succeeded OR failed with an already-attached
 * error (both mean "it's on the wheel"). `alreadyOnTarget` = items skipped
 * because they were already on the wheel. `failed` = items whose mutation failed
 * for a REAL reason. The union size the user cares about is
 * attached + alreadyOnTarget.
 *
 * `wheelCount` (bug #305) is how many TARGET wheels the batch was sent to —
 * defaults to 1 so every existing single-wheel caller reads exactly as
 * before ("to wheel", never "to wheels").
 *
 * Pure — returns { tone, message }, no side effects.
 */
export const bulkAttachResultMessage = ({
  attached = 0,
  alreadyOnTarget = 0,
  failed = 0,
  wheelCount = 1,
  captureCount,
} = {}) => {
  const union = attached + alreadyOnTarget;
  const useBulkCopy = typeof captureCount === "number" && captureCount >= 2;
  const captureWord = (n) => (n === 1 ? "capture" : "captures");
  const wheelWord = (n) => (n === 1 ? "wheel" : "wheels");
  const bulkSuccess = (captures, wheels) =>
    `Attached ${captures} ${captureWord(captures)} to ${wheels} ${wheelWord(wheels)}.`;

  if (useBulkCopy) {
    if (failed === 0) {
      return {
        tone: "success",
        message: bulkSuccess(captureCount, wheelCount),
      };
    }
    if (union === 0) {
      return {
        tone: "error",
        message: `Could not attach ${captureCount} ${captureWord(captureCount)} to ${wheelCount} ${wheelWord(wheelCount)}. Please try again.`,
      };
    }
    return {
      tone: "warning",
      message: `${bulkSuccess(captureCount, wheelCount)} The rest could not be attached — please try again.`,
    };
  }

  const noun = wheelCount > 1 ? "wheels" : "wheel";
  if (failed === 0) {
    return {
      tone: "success",
      message:
        union === 1 && wheelCount <= 1 ? "Linked to wheel." : `Added ${union} to ${noun}.`,
    };
  }
  if (union === 0) {
    return {
      tone: "error",
      message:
        failed === 1
          ? `Could not add to the ${noun}. Please try again.`
          : `Could not add ${failed} to the ${noun}. Please try again.`,
    };
  }
  return {
    tone: "warning",
    message: `Added ${union} of ${union + failed} to the ${noun}. The rest could not be added — please try again.`,
  };
};

/**
 * Sentinel the attach-wheel picker uses for its "Remove link" row (it is a
 * selectable option like a wheel id, but means "detach"). Lives here, next to
 * the validator that reads it, so the modal and its tests share one constant
 * instead of each declaring their own copy.
 */
export const ATTACH_UNATTACH = "__unattach__";

/**
 * Validate the attach-wheel picker before submit — the single source of truth
 * for "is Save actionable?", so the button's DISABLED state and the handler's
 * guard can never disagree.
 *
 * Returns a user-facing reason string, or null when the selection is
 * submittable. The reason is BOTH the disabled-button hint and the message the
 * handler surfaces, which is the point: a disabled control that states no
 * reason is a dead end (WCAG 3.3 — an error must be identified in text, not
 * implied by an inert button).
 *
 * `selected` accepts either a single wheel id (legacy single-select shape),
 * the `ATTACH_UNATTACH` sentinel, or (bug #305) an ARRAY of wheel ids for a
 * multi-wheel attach — normalized internally so every existing scalar caller
 * keeps behaving exactly as before.
 *
 * Mirrors validateComposer's shape for the New-capture composer.
 */
export const validateAttachSelection = ({
  selected = null,
  items = [],
  wheelsLoading = false,
  wheelsError = false,
  wheelCount = 0,
  canUnattach = false,
} = {}) => {
  if (wheelsLoading) return "Loading your wheels…";
  if (wheelsError) return "We couldn't load your wheels. Retry, then pick one.";
  if (wheelCount === 0 && !canUnattach) {
    return "You don't have any wheels yet — create one first.";
  }
  const isEmptySelection =
    selected === null ||
    selected === undefined ||
    (Array.isArray(selected) && selected.length === 0);
  if (isEmptySelection) {
    return canUnattach
      ? "Pick a wheel, or choose Remove link."
      : "Pick a wheel to add to.";
  }
  if (selected === ATTACH_UNATTACH) return null;

  const wheelIds = Array.isArray(selected) ? selected : [selected];
  // CAP-7 / bug #305: re-attaching a selection that is already entirely on
  // EVERY chosen wheel is a no-op. A SINGLE item already on the ONE chosen
  // wheel is a targeted detach (M2M) — Save stays enabled so we never fall
  // back to `{ wheel_id: null }`, which would drop every other link.
  const allDuplicate = wheelIds.every((wheelId) => isDuplicateAttach(items, wheelId));
  if (allDuplicate) {
    if (items.length === 1 && wheelIds.length === 1 && itemIsOnWheel(items[0], wheelIds[0])) {
      return null;
    }
    return wheelIds.length === 1
      ? "Already added to this wheel."
      : "Already added to every selected wheel.";
  }
  return null;
};

/* ----- toolbar: search + type filter + counts (deck states 04/14) ----- */

/**
 * The type-filter chips shown in the toolbar. All five backend-supported capture
 * types are offered as functional filters: the serializer now stores video/audio
 * too (Backend `capture` app, `CaptureType` = text/photo/doc/video/audio — the
 * A/V widening that "unblocks FE #2088"), so every chip can match real data.
 */
export const CAPTURE_FILTER_TYPES = [
  CAPTURE_TYPES.TEXT,
  CAPTURE_TYPES.PHOTO,
  CAPTURE_TYPES.DOC,
  CAPTURE_TYPES.VIDEO,
  CAPTURE_TYPES.AUDIO,
  CAPTURE_TYPES.LINK,
];

/** Special "no type filter" sentinel used by the "All" chip. */
export const FILTER_ALL = "all";

/**
 * Count items per capture type (+ a total under FILTER_ALL). Pure — drives the
 * chip badges. Unknown/other types are counted only in the total.
 */
export const typeCounts = (items = []) => {
  const counts = { [FILTER_ALL]: 0 };
  CAPTURE_FILTER_TYPES.forEach((t) => {
    counts[t] = 0;
  });
  (items || []).forEach((item) => {
    counts[FILTER_ALL] += 1;
    if (item && counts[item.type] != null) counts[item.type] += 1;
  });
  return counts;
};

/**
 * Strip punctuation so search is punctuation-insensitive (CAP-6).
 * Owned by ./captureSearchNormalize — see that module for why emoji are
 * deliberately NOT stripped (a blanket \p{S} strip made an emoji query
 * normalise to "" and therefore match the entire inbox).
 */
const stripPunctuation = (value) => normalizeCaptureSearch(value);

/**
 * The text a capture is searchable BY — everything the user can actually SEE on
 * the row, not just its note. The card shows, beyond the note (`text_content`):
 *   • a media Name (`name`) — the separate rename field (captureDisplayName),
 *   • the recovered file name inside `media_key` (QA-70), and
 *   • a LINK capture's URL/host (`source_url`), rendered on the card as e.g.
 *     "youtube.com".
 * Searching those was impossible before — a LINK visibly reading "youtube.com"
 * returned 0 for "youtube", and a note titled via `name` returned 0 for its own
 * words (capture headed sweep s21_search.png). The rule is simple: if it is on
 * the card, it is findable. `media_key` and `source_url` go in RAW — punctuation
 * is stripped by the same normaliser as the query, so "youtube.com" inside a
 * full URL still matches "youtube".
 */
export const captureSearchText = (item) =>
  [
    item?.text_content || "",
    item?.title || "",
    item?.name || "",
    item?.source_url || "",
    item?.media_key || "",
    captureTypeLabel(item?.type),
  ].join(" ");

/**
 * Filter capture items by the active type chip and a free-text query.
 * Query matches everything visible on the row (note, name, link URL/host,
 * recovered file name, human type label), case-insensitively AND
 * punctuation-insensitively. Pure + testable.
 */
export const filterCaptures = (items = [], { query = "", type = FILTER_ALL } = {}) => {
  const q = stripPunctuation((query || "").trim().toLowerCase());
  return (items || []).filter((item) => {
    if (!item) return false;
    if (type !== FILTER_ALL && item.type !== type) return false;
    if (!q) return true;
    const haystack = stripPunctuation(captureSearchText(item).toLowerCase());
    return haystack.includes(q);
  });
};

/**
 * Type-chip counts SCOPED TO THE ACTIVE SEARCH.
 *
 * The chips sit directly above the list, so their badges are read as "how many
 * of these will I see if I tap this chip". Counting the unfiltered set while
 * the list is search-filtered makes the toolbar contradict the screen — e.g.
 * searching "invoice" leaves one row visible under a chip reading `Photo 12`.
 *
 * The query is applied, the TYPE filter deliberately is not: a chip's own badge
 * must not collapse to the count of the chip that happens to be active, or
 * every unselected chip would read 0 and the row would stop being navigable.
 * So each badge answers exactly "tap me and you get N rows".
 */
export const typeCountsForQuery = (items = [], query = "") =>
  typeCounts(filterCaptures(items, { query, type: FILTER_ALL }));

/* ----- paged inbox: counts must never lie about pages not yet fetched ----- */

/**
 * Placeholder shown for a chip whose real count is not known yet, because the
 * inbox has more pages than have been fetched.
 *
 * MEASURED DEFECT (staging, bundle main.8b745d1a.js, 2026-07-30): with 35
 * captures (30 note / 2 photo / 1 doc / 1 video / 1 audio) and a page size of
 * 20, the toolbar rendered `All 20 · Note 20 · Photo 0 · Document 0 · Video 0 ·
 * Audio 0` while the page header correctly read 35 — the counts were computed
 * over the LOADED pages only. Tapping Video (badge `0`) rendered "No matching
 * captures" and fired ZERO network requests: the user was told, confidently and
 * in plain language, that they own no videos while owning one.
 *
 * A zero the app has not actually verified is the worst possible value to
 * render, so an unknown count renders as this marker instead — the chip stays
 * tappable, and tapping it goes and finds out (see shouldDrainForFilter).
 */
export const CHIP_COUNT_PARTIAL = "…";

/**
 * Render a chip badge honestly.
 *   complete  → the exact number ("1", "0" — a verified zero is fine).
 *   partial   → "N+" when at least N are already known, or the PARTIAL marker
 *               when none have been seen yet (which is NOT the same as none
 *               existing).
 */
export const formatChipCount = (count = 0, { complete = true } = {}) => {
  const n = Number(count) || 0;
  if (complete) return String(n);
  return n > 0 ? `${n}+` : CHIP_COUNT_PARTIAL;
};

/** True when a search query or a type chip is narrowing the list. */
export const isFiltering = ({ query = "", type = FILTER_ALL } = {}) =>
  Boolean((query || "").trim()) || type !== FILTER_ALL;

/**
 * Should the inbox keep fetching pages because a filter is active?
 *
 * The backend list endpoint has NO type filter and NO per-type counts (see
 * capture/api/viewsets.py: the only list filter is `attached_wheel`), so the
 * client cannot ask the server "how many videos do I have?" — the only honest
 * client-side answer is to fetch the remaining pages and then count. Filtering
 * therefore DRAINS the query instead of short-circuiting on what happens to be
 * in memory: clicking a chip really does go to the server.
 */
export const shouldDrainForFilter = ({
  filtering = false,
  hasNextPage = false,
  isFetchingNextPage = false,
} = {}) => Boolean(filtering && hasNextPage && !isFetchingNextPage);

/**
 * Which body the filtered list should render:
 *   'rows'      → there are matches to show.
 *   'searching' → nothing matched YET, but pages are still coming in. Claiming
 *                 "No matching captures" here is the false negative itself.
 *   'empty'     → nothing matched and the whole inbox has been seen.
 */
export const captureListPhase = ({
  visibleCount = 0,
  filtering = false,
  hasNextPage = false,
  isFetchingNextPage = false,
} = {}) => {
  if (visibleCount > 0) return "rows";
  if (filtering && (hasNextPage || isFetchingNextPage)) return "searching";
  return "empty";
};

/* ----- bulk select: header select-all checkbox tri-state (§15 law) ----- */

/**
 * Tri-state for the "select all" header checkbox (deck round-8/9 §15 law):
 *   'none'  → unchecked   (0 selected)
 *   'some'  → indeterminate (0 < selected < total)
 *   'all'   → checked      (selected === total, total > 0)
 * Pure — mirrors the app's StorageTable header-checkbox wiring.
 */
export const headerCheckboxState = (selectedCount = 0, totalCount = 0) => {
  if (totalCount > 0 && selectedCount >= totalCount) return "all";
  if (selectedCount > 0) return "some";
  return "none";
};

/**
 * True when `wheelId` is in the picker's checked list (string-safe).
 */
export const isWheelSelected = (selectedIds = [], wheelId) =>
  (selectedIds || []).some((id) => String(id) === String(wheelId));

/**
 * Toggle one id within a Set-like array of selected ids (immutable).
 * Returns a new array.
 */
export const toggleSelected = (selectedIds = [], id) => {
  const key = String(id);
  const has = (selectedIds || []).some((x) => String(x) === key);
  return has
    ? selectedIds.filter((x) => String(x) !== key)
    : [...selectedIds, id];
};

/**
 * Busy state for the error-card Retry button (ISP-4068 C).
 *
 * A retry is a background refetch (the query status is "error", not "loading"),
 * so we key the button's spinner/disabled state off the refetch flag. Accepts
 * the react-query result's fetching flags and returns true while a retry is in
 * flight, so the button shows an immediate spinner and can't be double-fired.
 */
export const isRetryBusy = ({ isFetching, isRefetching, isLoading } = {}) =>
  Boolean(isRefetching || (isFetching && !isLoading));

/* ----- detach (CAP-detach) ----------------------------------------------- */

/**
 * Copy for the "detach this capture from its Wheel" confirm dialog.
 *
 * Pure so the wording is unit-testable without a DOM, and so both surfaces that
 * can detach (the Capture inbox card and the wheel-side "Captured items on this
 * wheel" drawer) speak with ONE voice instead of hand-rolling two dialogs.
 *
 * Deliberately NOT the delete copy: detaching is reversible — the capture stays
 * in the inbox and can be attached again — so it overrides the shared confirm's
 * default "This action cannot be undone." note, and labels its primary button
 * "Detach" rather than the ambiguous "Confirm".
 *
 * @param {string} [wheelName] the wheel's display name, when the surface knows
 *   it (the inbox list only receives `attached_wheel` as an id, so it does not).
 */
export const detachConfirmCopy = (wheelName) => {
  const names = Array.isArray(wheelName)
    ? wheelName.filter((name) => name != null && String(name).trim() !== "")
    : wheelName
    ? [wheelName]
    : [];
  let subtitle;
  if (names.length === 1) {
    subtitle = `This capture will be removed from “${names[0]}”. It stays in your Capture inbox.`;
  } else if (names.length > 1) {
    subtitle = `This capture will be removed from ${names.length} Wheels. It stays in your Capture inbox.`;
  } else {
    subtitle =
      "This capture will be removed from the Wheel it is attached to. It stays in your Capture inbox.";
  }
  return {
    title: "Detach from Wheel?",
    subtitle,
    note: "You can attach it to a Wheel again at any time.",
    confirmLabel: "Detach",
  };
};

/* ----- smart detach (ISP-9999 multi-wheel) ------------------------------- */

/**
 * The wheels a single capture is attached to, as `[{ id, name }]`.
 *
 * Reads the M2M `attached_wheels` list the backend now serves (#2470), each
 * `{ id, name }`. Falls back to the legacy singular `attached_wheel` (+ its
 * `attached_wheel_name`) when the plural is absent — an older cached row, or a
 * backend that has not shipped the M2M read yet — so a single-wheel capture is
 * never treated as attached to nothing. De-duplicates by id and drops entries
 * with no id. Pure, so the single-vs-multi decision below is unit-testable
 * without a DOM.
 */
export const attachedWheelsOf = (item) => {
  const out = [];
  const seen = new Set();
  const push = (id, name) => {
    if (id == null || id === "" || seen.has(id)) return;
    seen.add(id);
    out.push({ id, name: (name || "").trim() });
  };
  const plural = Array.isArray(item?.attached_wheels) ? item.attached_wheels : [];
  plural.forEach((w) => push(w?.id, w?.name));
  // Fold the legacy singular in too, so a not-yet-migrated row still counts.
  if (item?.attached_wheel != null && item.attached_wheel !== "") {
    push(item.attached_wheel, item.attached_wheel_name);
  }
  return out;
};

/** How many distinct wheels the capture is attached to. */
export const attachedWheelCount = (item) => attachedWheelsOf(item).length;

/**
 * Decide how detaching this capture should behave (ISP-9999, Gordon 2026-08-18:
 * "if it is a single attached wheel then simply detach it; if multiple wheels
 * bring up the list of attached to select which wheels they want to
 * disconnect").
 *
 *   - 0 wheels  → `mode: "none"`   — nothing to detach (guard; the control is
 *                 not shown on an unattached capture).
 *   - 1 wheel   → `mode: "single"` — detach it directly, NO picker; `wheelIds`
 *                 carries the one id to send straight to the detach mutation.
 *   - 2+ wheels → `mode: "multi"`  — the caller opens the naming selection
 *                 dialog so the user chooses which wheel(s) to disconnect.
 *
 * Pure — no dispatch, no DOM — so the branch the UI takes is asserted in a
 * unit test rather than only in a heavy page render.
 */
export const planCaptureDetach = (item) => {
  const wheels = attachedWheelsOf(item);
  let mode = "none";
  if (wheels.length === 1) mode = "single";
  else if (wheels.length > 1) mode = "multi";
  return { mode, wheels, wheelIds: wheels.map((w) => w.id) };
};

/**
 * Remove one capture id from a react-query INFINITE-query cache payload
 * (`{ pages: [{ count, next, previous, results: [] }], pageParams }`).
 *
 * Used for the optimistic detach on the wheel-side drawer: the row disappears
 * the moment the user confirms rather than after a round-trip, and the DRF
 * `count` is decremented in step so the drawer's "N items" header summary does
 * not disagree with the list it sits above. Returns the input untouched when
 * there is nothing to remove, so the caller can roll back to the exact previous
 * object reference if the request fails.
 *
 * Pure — no cache access, no mutation of the input.
 */
export const removeCaptureFromPages = (data, id) => {
  if (!data || !Array.isArray(data.pages)) return data;
  let removed = 0;
  const pages = data.pages.map((page) => {
    const results = page?.results ?? [];
    const kept = results.filter((it) => it?.id !== id);
    if (kept.length === results.length) return page;
    removed += results.length - kept.length;
    return { ...page, results: kept };
  });
  if (!removed) return data;
  return {
    ...data,
    pages: pages.map((page) =>
      typeof page?.count === "number"
        ? { ...page, count: Math.max(0, page.count - removed) }
        : page
    ),
  };
};
